import { FlaskConical, Loader2 } from 'lucide-react'

import { EvaluationAnalysisDetail } from '@/components/evaluation/evaluation-analysis-detail'
import { rubricSectionHighlight } from '@/components/admin/evaluation-rubrics/rubric-section-ids'
import { Badge } from '@/components/ui/badge'
import { EntityManageSection } from '@/components/ui/entity-manage-section'
import { Button } from '@/components/ui/button'
import type { EvaluationAnalysisPayload } from '@/hooks/use-evaluation-analysis'

type RubricSampleTestCardProps = {
  subjectLabel?: string
  isRunning: boolean
  errorMessage?: string | null
  payload?: EvaluationAnalysisPayload | null
  highlighted?: boolean
  /** True when draft criteria/scoring changed after the last sample run. */
  isStale?: boolean
  onRun: () => void
  onOpenFullReport?: () => void
}

export function RubricSampleTestCard({
  subjectLabel,
  isRunning,
  errorMessage,
  payload,
  highlighted,
  isStale = false,
  onRun,
  onOpenFullReport,
}: RubricSampleTestCardProps) {
  return (
    <div id={rubricSectionHighlight('test')}>
      <EntityManageSection
        title="Sample analysis"
        subtitle="See if this rubric ranks the Simulation sample the way we expect"
        leadingIcon={FlaskConical}
        highlighted={highlighted}
        agentHighlight={rubricSectionHighlight('test')}
      >
        <div className="space-y-4">
          {subjectLabel ? (
            <p className="text-sm text-muted-foreground">Sample: {subjectLabel}</p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Uses tenant demo seed profile and job (configure via platform.rubricPreviewSample).
            </p>
          )}
          {isStale && payload ? (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-sm">
              <Badge variant="warningSoft">
                Stale
              </Badge>
              <p className="text-muted-foreground">
                Criteria or scoring changed since this run. Run again before trusting the report.
              </p>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={onRun} disabled={isRunning}>
              {isRunning ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />
                  Running sample analysis…
                </>
              ) : isStale && payload ? (
                'Run again'
              ) : (
                'Run sample analysis'
              )}
            </Button>
            {payload && onOpenFullReport ? (
              <Button
                type="button"
                variant="outline"
                onClick={onOpenFullReport}
                disabled={isStale}
                title={isStale ? 'Run again to refresh the report' : undefined}
              >
                Open full report
              </Button>
            ) : null}
          </div>
          {errorMessage ? <p className="text-sm text-destructive">{errorMessage}</p> : null}
          {payload ? (
            <div
              className={`rounded-md border p-4 ${isStale ? 'opacity-60 pointer-events-none' : ''}`}
              aria-disabled={isStale || undefined}
            >
              <EvaluationAnalysisDetail payload={payload} subjectLabel={subjectLabel} />
            </div>
          ) : null}
        </div>
      </EntityManageSection>
    </div>
  )
}
